import { i18NextKeys } from '~/common/generated/i18n.generated'

/**
 * Maps a formula's smallint error code (from `{col_id}_error` in the z-table, surfaced via
 * `TableCell.formulaError`) to the i18n key for its display message.
 *
 * Mirrors `FormulaErrorCode` in `packages/common-z-tables/src/ddl/formula/formula-error-codes.ts`
 * (backend-only, not importable here) -- keep in sync if that catalog changes. Code 0
 * (NULL_OUTPUT) is not an error -- it means the formula legitimately evaluated to null -- so it
 * is intentionally excluded here.
 */
/**
 * Every non-zero code in the backend catalog. Widen this union first when a code is added there,
 * so the Record below fails to compile until the matching message key exists.
 */
type KnownFormulaErrorCode = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10

const FORMULA_ERROR_MESSAGE_KEYS: Record<KnownFormulaErrorCode, i18NextKeys> = {
  1: 'table_cells.formula_error.division_by_zero',
  2: 'table_cells.formula_error.to_date_parse_failure',
  3: 'table_cells.formula_error.to_number_parse_failure',
  4: 'table_cells.formula_error.invalid_date_components',
  5: 'table_cells.formula_error.ifs_no_match',
  6: 'table_cells.formula_error.left_negative_count',
  7: 'table_cells.formula_error.right_negative_count',
  8: 'table_cells.formula_error.mid_start_below_one',
  9: 'table_cells.formula_error.mid_negative_length',
  10: 'table_cells.formula_error.numeric_overflow',
}

// Used to mask numeric only formula errors when NumericFormulaRuntimeErrors is off.
const ALWAYS_NUMERIC_FORMULA_ERROR_CODES: ReadonlySet<number> = new Set([1, 3, 10])

// IFS_NO_MATCH can't be judged from the code alone -- IFS can output a number, string, date, or
// datetime depending on its branches, so the same code shows up on both numeric and non-numeric
// IFS formulas. Only mask it when we separately know THIS formula's own output is numeric.
const IFS_NO_MATCH_CODE = 5

/**
 * Returns the i18n key for a formula error code's display message, or null if the code isn't a
 * real error (0, or not yet in the catalog above -- falls back to the generic "unknown" message
 * for any positive code we don't recognize yet, since new codes are append-only on the backend).
 *
 * @param isNumericFormula - Whether this cell's formula outputs a number.
 * @param numericFormulaRuntimeErrorsEnabled - Whether the org has NumericFormulaRuntimeErrors on.
 *   Checking the flag here too means turning it off hides those old errors, instead of waiting for the row to recompute.
 */
export function getFormulaErrorMessageKey(
  errorCode: number | null | undefined,
  isNumericFormula: boolean,
  numericFormulaRuntimeErrorsEnabled: boolean,
): i18NextKeys | null {
  if (errorCode == null || errorCode === 0) {
    return null
  }
  if (!numericFormulaRuntimeErrorsEnabled) {
    if (ALWAYS_NUMERIC_FORMULA_ERROR_CODES.has(errorCode)) {
      return null
    }
    if (errorCode === IFS_NO_MATCH_CODE && isNumericFormula) {
      return null
    }
  }
  return isKnownFormulaErrorCode(errorCode)
    ? FORMULA_ERROR_MESSAGE_KEYS[errorCode]
    : 'table_cells.formula_error.unknown'
}

const isKnownFormulaErrorCode = (code: number): code is KnownFormulaErrorCode => {
  return code in FORMULA_ERROR_MESSAGE_KEYS
}
